import type { Action, Epitaph, MatchEvent, MatchState } from '../engine';
import type { PositionSpec } from './position';

/**
 * The interactive tutorial: short lessons, each a position on the real board and one thing to
 * do in it. North has already passed in every lesson, so the player acts alone. Each lesson
 * judges the player's actions from the events they produce; its texts live in src/i18n under
 * tutorial.<id>.*. The steps of `solution` solve it (tests replay them).
 */

export type LessonId =
  | 'turn'
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'capture'
  | 'match'
  | 'chain'
  | 'crown'
  | 'wild'
  | 'epitaph'
  | 'recover'
  | 'pass';

/** One action of the player and what it did. */
export interface LessonStep {
  readonly before: MatchState;
  readonly action: Action;
  readonly after: MatchState;
  readonly events: readonly MatchEvent[];
}

export type LessonOutcome =
  /** Not finished yet (e.g. in the middle of a chain). */
  | { readonly kind: 'continue' }
  /** Solved; `message` picks the success text, `score` the points scored. */
  | { readonly kind: 'done'; readonly message: 'done' | 'donePlus'; readonly score: number }
  /** Not what the lesson asked for: the lesson restarts. */
  | { readonly kind: 'retry' };

/** A step of a lesson's solution, in board notation. */
export type SolutionStep =
  | { readonly type: 'continue' | 'pass' | 'endChain' }
  | { readonly type: 'link'; readonly notation: string; readonly collateral?: string }
  | { readonly type: 'epitaph'; readonly from: string }
  | { readonly type: 'recover'; readonly card: string };

export interface Lesson {
  readonly id: LessonId;
  readonly position: PositionSpec;
  /** Judges each action; `history` holds the events of every action since the lesson started. */
  evaluate(step: LessonStep, history: readonly MatchEvent[]): LessonOutcome;
  readonly solution: readonly SolutionStep[];
}

const CONTINUE: LessonOutcome = { kind: 'continue' };
const RETRY: LessonOutcome = { kind: 'retry' };
const done = (score: number, plus = false): LessonOutcome => ({
  kind: 'done',
  message: plus ? 'donePlus' : 'done',
  score,
});

/** The score of the chain or Epitaph that ended with this step, if one did. */
function scored(step: LessonStep): Extract<MatchEvent, { type: 'ChainScored' }> | undefined {
  return step.events.find(
    (e): e is Extract<MatchEvent, { type: 'ChainScored' }> =>
      e.type === 'ChainScored' && e.side === 'S',
  );
}

/** The last chain South completed, as the links played and whether anything was captured. */
function lastChain(history: readonly MatchEvent[]) {
  const scoredAt = history.findLastIndex((e) => e.type === 'ChainScored' && e.side === 'S');
  const before = history.slice(0, scoredAt);
  const start = before.findLastIndex((e) => e.type === 'ChainScored' && e.side === 'S');
  const events = history.slice(start + 1, scoredAt + 1);
  return {
    links: events.filter(
      (e): e is Extract<MatchEvent, { type: 'LinkPlayed' }> =>
        e.type === 'LinkPlayed' && e.side === 'S',
    ),
    captures: events.filter((e) => e.type === 'PawnCaptured' && e.side === 'N').length,
    crowned: events.some((e) => e.type === 'PawnCrowned' && e.side === 'S'),
  };
}

/** A lesson solved by any chain whose links all have one color. */
const colorLesson = (
  id: LessonId,
  color: 'R' | 'B' | 'G',
  position: PositionSpec,
  solution: string,
): Lesson => ({
  id,
  position,
  evaluate(step, history) {
    const score = scored(step);
    if (!score) return CONTINUE;
    const { links } = lastChain(history);
    return links.length > 0 && links.every((l) => l.step.face.color === color)
      ? done(score.breakdown.score)
      : RETRY;
  },
  solution: [{ type: 'link', notation: solution }],
});

/** The Epitaph the tutorial hands the player: R2 two squares forward, then B1 up and right. */
const TUTORIAL_EPITAPH: Epitaph = {
  id: 'tutorial-epitaph',
  owner: 'S',
  sourcePawnId: 'S-C',
  inscription: {
    links: [
      { face: { color: 'R', number: 2 }, wasWild: false, move: { type: 'slide', dx: 0, dy: 2 } },
      { face: { color: 'B', number: 1 }, wasWild: false, move: { type: 'slide', dx: 1, dy: 1 } },
    ],
    recordedScore: 4,
    round: 1,
  },
};

export const LESSONS: readonly Lesson[] = [
  {
    id: 'turn',
    position: {
      phase: 'TURN_START',
      hands: { S: ['R1', 'B2', 'G3', 'Y2', 'R2'] },
      passed: ['N'],
    },
    evaluate: (step) =>
      step.action.type === 'CONTINUE' ? done(0) : step.action.type === 'PASS' ? RETRY : CONTINUE,
    solution: [{ type: 'continue' }],
  },
  colorLesson('red', 'R', { south: ['C1', 'E1'], hands: { S: ['R2'] }, passed: ['N'] }, 'R2 C1-C3'),
  colorLesson(
    'blue',
    'B',
    { south: ['C1', 'B2'], hands: { S: ['B2'] }, passed: ['N'] },
    'B2 C1-E3',
  ),
  colorLesson(
    'green',
    'G',
    { south: ['A1', 'B1', 'B2', 'C2'], hands: { S: ['G1'] }, passed: ['N'] },
    'G1 B1-C3',
  ),
  {
    id: 'yellow',
    position: {
      south: ['B1', 'F1'],
      north: ['C2', 'E4', 'A6', 'F6'],
      hands: { S: ['Y2'] },
      passed: ['N'],
    },
    evaluate(step, history) {
      const score = scored(step);
      if (!score) return CONTINUE;
      return lastChain(history).captures >= 2 ? done(score.breakdown.score) : RETRY;
    },
    solution: [{ type: 'link', notation: 'Y2 B1-D3-F5' }],
  },
  {
    id: 'capture',
    position: {
      south: ['C2', 'E4'],
      north: ['C4', 'E6', 'A6'],
      hands: { S: ['R2'] },
      passed: ['N'],
    },
    evaluate(step, history) {
      const score = scored(step);
      if (!score) return CONTINUE;
      return lastChain(history).captures > 0 ? done(score.breakdown.score) : RETRY;
    },
    solution: [{ type: 'link', notation: 'R2 C2-C4' }],
  },
  {
    id: 'match',
    position: {
      south: ['C1', 'E1'],
      top: 'B2',
      hands: { S: ['R1', 'G2', 'B3', 'Y1'] },
      passed: ['N'],
    },
    evaluate(step) {
      const score = scored(step);
      return score ? done(score.breakdown.score) : CONTINUE;
    },
    solution: [{ type: 'link', notation: 'G2 C1-D3' }],
  },
  {
    id: 'chain',
    position: {
      south: ['B1', 'F1'],
      north: ['A6', 'F6'],
      hands: { S: ['R1', 'R2', 'R3'] },
      passed: ['N'],
    },
    evaluate(step) {
      const score = scored(step);
      if (!score) return CONTINUE;
      const m = score.breakdown.multiplier;
      if (m.links < 3) return RETRY;
      return done(score.breakdown.score, m.pattern === 'ladder');
    },
    solution: [
      { type: 'link', notation: 'R1 B1-B2' },
      { type: 'link', notation: 'R2 B2-B4' },
      { type: 'link', notation: 'R3 B4-E4' },
    ],
  },
  {
    id: 'crown',
    position: {
      south: ['D4', 'A1'],
      north: ['A6', 'B6', 'F6'],
      hands: { S: ['R2', 'R1'] },
      passed: ['N'],
    },
    evaluate(step, history) {
      const score = scored(step);
      if (!score) return CONTINUE;
      const chain = lastChain(history);
      if (!chain.crowned) return RETRY;
      // The extra praise: moving backward after crowning.
      const back = chain.links.some((l) => {
        const from = l.step.from;
        const to = l.step.landings[l.step.landings.length - 1];
        return !!to && to.y < from.y;
      });
      return done(score.breakdown.score, back);
    },
    solution: [
      { type: 'link', notation: 'R2 D4-D6' },
      { type: 'link', notation: 'R1 D6-D5' },
    ],
  },
  {
    id: 'wild',
    position: {
      south: ['C2', 'E1', 'A1'],
      north: ['D4', 'A6', 'F6'],
      top: 'R3',
      hands: { S: ['W', 'Y1', 'B1'] },
      passed: ['N'],
    },
    evaluate(step, history) {
      const score = scored(step);
      if (!score) return CONTINUE;
      const chain = lastChain(history);
      if (!chain.links.some((l) => l.step.wasWild)) return RETRY;
      return done(score.breakdown.score, chain.captures > 0);
    },
    solution: [{ type: 'link', notation: 'W=G3 C2-D4', collateral: 'E1' }],
  },
  {
    id: 'epitaph',
    position: {
      south: ['B1', 'E2'],
      north: ['C4', 'A6', 'F6'],
      top: 'Y3',
      hands: { S: ['Y1'] },
      epitaphs: { S: [TUTORIAL_EPITAPH] },
      passed: ['N'],
    },
    evaluate(step) {
      const played = step.events.find((e) => e.type === 'EpitaphPlayed');
      if (played && played.type === 'EpitaphPlayed')
        return done(
          played.score,
          step.events.some((e) => e.type === 'PawnCaptured' && e.side === 'N'),
        );
      return step.action.type === 'CONTINUE' ? CONTINUE : RETRY;
    },
    solution: [{ type: 'epitaph', from: 'B1' }],
  },
  {
    id: 'recover',
    position: {
      south: ['C1', 'D1'],
      top: 'R1',
      hands: { S: ['G3', 'Y2', 'B3'] },
      passed: ['N'],
    },
    evaluate: (step) =>
      step.events.some((e) => e.type === 'Recovered' && e.side === 'S') ? done(0) : RETRY,
    solution: [{ type: 'recover', card: 'G3' }],
  },
  {
    id: 'pass',
    position: {
      phase: 'TURN_START',
      hands: { S: ['R1', 'B2', 'G3', 'Y1', 'R2'] },
      scores: { S: 34, N: 21 },
      passed: ['N'],
    },
    evaluate(step) {
      const ended = step.events.find((e) => e.type === 'RoundEnded');
      if (ended && ended.type === 'RoundEnded') return ended.winner === 'S' ? done(34) : RETRY;
      return RETRY;
    },
    solution: [{ type: 'pass' }],
  },
];
